import type { BaseQueryFn, QueryActionCreatorResult, QueryDefinition } from '@reduxjs/toolkit/query';
import DateTimeOrNoData from 'components/content/DateTimeOrNoData';
import NoData from 'components/content/NoData';
import ErrorDialog from 'components/overlays/ErrorDialog';
import type { SortConfig, TableColumn } from 'components/table';
import { DataTable, getNextSortConfig, sortData } from 'components/table';
import { SystemAdminDialogTypes, SystemAdminFormTypes } from 'helper/types';
import { useCan } from 'hooks/useCan';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    ActiveSidebar,
    clearActiveElements,
    getActiveSidebar,
    getSelectedFleet,
    getSystemAdminOpenDialog,
    setActiveSidebar,
    setSelectedFleet,
    setSystemAdminOpenDialog,
} from 'store/slices/settingsPageSlice';
import {
    clearFleetFormData,
    getSystemFleetsFormType,
    setFormFleetName,
    setSystemFleetsFormType,
} from 'store/slices/systemFleet';

import ExitEditingDialog from '../ExitEditingDialog';
import DialogForm from '../FormDialog';
import SystemFleetsForm from './SystemFleetsForm';

import type { SystemFleet } from 'API';

interface Props {
    data: SystemFleet[] | undefined;
    isLoading?: boolean;
    isFetching?: boolean;
    isError?: boolean;
    refetch(): QueryActionCreatorResult<
        QueryDefinition<string, BaseQueryFn<any, unknown, unknown, {}, {}>, never, SystemFleet[], 'systemFleets'>
    >;
}

const SystemFleetsTable = ({ data, isLoading, isFetching, isError, refetch }: Props) => {
    const fleets = data ?? [];

    const columns = useMemo<TableColumn<SystemFleet>[]>(
        () => [
            {
                key: 'name',
                title: 'Name',
                dataIndex: 'name',
                sortable: true,
            },
            {
                key: 'numberOfVehicles',
                title: 'No. of vehicles',
                dataIndex: 'numberOfVehicles',
                sortable: true,
            },
            {
                key: 'teams',
                title: 'Team assigned',
                dataIndex: 'teams',
                sortable: false,
                render: (_, fleet) =>
                    fleet.teams?.length ? (
                        fleet.teams.map(team => team.name).join(', ')
                    ) : (
                        <NoData text='No data' className='text-size-14' />
                    ),
            },
            {
                key: 'createdAt',
                title: 'Created',
                dataIndex: 'createdAt',
                sortable: true,
                render: (_, fleet) => <DateTimeOrNoData value={fleet.createdAt} />,
                sortValue: fleet => new Date(fleet.createdAt ?? ''),
            },
        ],
        []
    );

    const activeSidebar = useSelector(getActiveSidebar);

    const dispatch = useDispatch();
    const systemAdminOpenDialog = useSelector(getSystemAdminOpenDialog);
    const isFormDialogOpen = systemAdminOpenDialog === SystemAdminDialogTypes.FLEET_DIALOG;
    const exitConfirmationIsOpen = systemAdminOpenDialog === SystemAdminDialogTypes.EXIT_CONFIRMATION;
    const errorDialogIsOpen = systemAdminOpenDialog === SystemAdminDialogTypes.ERROR_DIALOG;
    const systemFleetForm = useSelector(getSystemFleetsFormType);
    const [sortConfig, setSortConfig] = useState<SortConfig<string>>({ key: 'name', dir: 'asc' });
    const { can: canEditFleets } = useCan({ type: 'fleets', action: 'edit' });

    const setDialogHeader = useMemo(() => {
        switch (systemFleetForm) {
            case SystemAdminFormTypes.EDIT_FORM:
                return 'Edit fleet';
            case SystemAdminFormTypes.CREATE_FORM:
                return 'Add information about the new fleet';
            default:
                return;
        }
    }, [systemFleetForm]);

    const closeDialogs = () => {
        dispatch(setSystemAdminOpenDialog(undefined));
    };

    const openCancelDialog = () => {
        dispatch(setSystemAdminOpenDialog(SystemAdminDialogTypes.EXIT_CONFIRMATION));
    };

    const openEditFleetDialog = (fleet: SystemFleet) => {
        dispatch(setSelectedFleet(fleet));
        dispatch(setFormFleetName(fleet.name));
        dispatch(setSystemAdminOpenDialog(SystemAdminDialogTypes.FLEET_DIALOG));
        dispatch(setSystemFleetsFormType(SystemAdminFormTypes.EDIT_FORM));
    };
    const exitEditing = () => {
        dispatch(clearFleetFormData());
        dispatch(clearActiveElements());
        closeDialogs();
    };

    const keepEditing = () => {
        dispatch(setSystemAdminOpenDialog(SystemAdminDialogTypes.FLEET_DIALOG));
    };

    const generateFleetActions = (fleet: SystemFleet) =>
        canEditFleets
            ? [
                  {
                      id: 'edit',
                      title: 'Edit',
                      icon: 'rioglyph-pencil',
                      onSelect: () => openEditFleetDialog(fleet),
                  },
              ]
            : [];

    const sortedFleetData = useMemo(() => sortData(fleets, sortConfig, columns), [fleets, sortConfig, columns]);

    const selectedFleet = useSelector(getSelectedFleet);

    return (
        <div className='display-flex flex-column' data-testid='system-fleets-table-wrapper'>
            <DataTable
                data-testid='system-fleets-table'
                stickyHeader
                rowKey='id'
                columns={columns}
                dataSource={sortedFleetData}
                activeRowKey={activeSidebar === ActiveSidebar.FLEETS ? selectedFleet?.id : undefined}
                sortConfig={sortConfig}
                onSortChange={key => setSortConfig(prev => getNextSortConfig(prev, key))}
                onRowClick={fleet => {
                    dispatch(setSelectedFleet(fleet));
                    dispatch(setActiveSidebar(ActiveSidebar.FLEETS));
                }}
                isLoading={isLoading}
                loadMore={{
                    loaded: sortedFleetData.length,
                    total: sortedFleetData.length,
                }}
                actionColumn={{
                    ariaLabel: fleet => `Actions for ${fleet.name}`,
                    disabled: !canEditFleets,
                    actions: fleet => generateFleetActions(fleet),
                }}
                isError={isError}
                error={{
                    header: 'Failed to load fleets',
                    message: 'If this issue persists, please contact the Control Center support team.',
                    onClick: () => refetch(),
                }}
                empty={{
                    title: 'No fleets yet, but they will appear here',
                    body: 'As of now, there are no fleets in the Control Center.',
                }}
            />
            <DialogForm
                id='edit-fleet'
                title={setDialogHeader}
                form={<SystemFleetsForm refetch={refetch} isFetchingFleets={isFetching} />}
                show={isFormDialogOpen}
                onCancel={openCancelDialog}
            />
            <ExitEditingDialog
                id='exit-fleet-edit'
                show={exitConfirmationIsOpen}
                onConfirm={exitEditing}
                onCancel={keepEditing}
            />
            <ErrorDialog show={errorDialogIsOpen} onConfirm={exitEditing} />
        </div>
    );
};

export default SystemFleetsTable;
